import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiRequest } from "../../../common/api/apiClient.js";
import type { BootstrapPasswordStoreRequest, CreatedPasswordPublicLinkDTO, CreatePasswordEntryRequest, CreatePasswordPublicLinkRequest, CreatePasswordWorkspaceRequest, PasswordEntryDTO, PasswordKeyringDTO, PasswordKeyringStateDTO, PasswordPublicLinkDTO, PasswordRecipientKeyDTO, PasswordWorkspaceDTO, PublicPasswordSecretDTO, ShareDTO, SharePasswordWorkspaceRequest, UpdatePasswordEntryRequest } from "../../../common/api/types.js";

export const passwordKeyringKey = ["passwords", "keyring"] as const;
export const passwordWorkspacesKey = ["passwords", "workspaces"] as const;
export const passwordEntriesKey = (workspaceId: string) => ["passwords", "entries", workspaceId] as const;
export const passwordSharesKey = (workspaceId: string) => ["passwords", "shares", workspaceId] as const;
export const passwordPublicLinksKey = (entryId: string) => ["passwords", "public-links", entryId] as const;

export async function readPasswordKeyring(): Promise<PasswordKeyringDTO | null> {
  return (await apiRequest<PasswordKeyringStateDTO>("/passwords/keyring")).keyring;
}

export function usePasswordKeyring() {
  return useQuery({ queryKey: passwordKeyringKey, queryFn: readPasswordKeyring });
}

export function useBootstrapPasswordStore() {
  const client = useQueryClient();
  return useMutation<void, Error, BootstrapPasswordStoreRequest>({
    mutationFn: (body) => apiRequest<void>("/passwords/bootstrap", { method: "POST", body }),
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: passwordKeyringKey }), client.invalidateQueries({ queryKey: passwordWorkspacesKey })]).then(() => undefined),
  });
}

export function usePasswordWorkspaces(enabled: boolean) {
  return useQuery({ queryKey: passwordWorkspacesKey, queryFn: () => apiRequest<PasswordWorkspaceDTO[]>("/passwords/workspaces"), enabled });
}

export function useCreatePasswordWorkspace() {
  const client = useQueryClient();
  return useMutation<PasswordWorkspaceDTO, Error, CreatePasswordWorkspaceRequest>({ mutationFn: (body) => apiRequest("/passwords/workspaces", { method: "POST", body }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordWorkspacesKey }) });
}

export function usePasswordEntries(workspaceId: string | null, enabled: boolean) {
  return useQuery({ queryKey: passwordEntriesKey(workspaceId ?? ""), queryFn: () => apiRequest<PasswordEntryDTO[]>(`/passwords/workspaces/${workspaceId as string}/entries`), enabled: enabled && workspaceId !== null });
}

export function useCreatePasswordEntry(workspaceId: string) {
  const client = useQueryClient();
  return useMutation<PasswordEntryDTO, Error, CreatePasswordEntryRequest>({ mutationFn: (body) => apiRequest(`/passwords/workspaces/${workspaceId}/entries`, { method: "POST", body }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordEntriesKey(workspaceId) }) });
}

export function useUpdatePasswordEntry(workspaceId: string, entryId: string) {
  const client = useQueryClient();
  return useMutation<PasswordEntryDTO, Error, UpdatePasswordEntryRequest>({ mutationFn: (body) => apiRequest(`/passwords/workspaces/${workspaceId}/entries/${entryId}`, { method: "PUT", body }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordEntriesKey(workspaceId) }) });
}

export function useDeletePasswordEntry(workspaceId: string) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({ mutationFn: (entryId) => apiRequest(`/passwords/workspaces/${workspaceId}/entries/${entryId}`, { method: "DELETE" }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordEntriesKey(workspaceId) }) });
}

export function usePasswordShares(workspaceId: string, enabled: boolean) {
  return useQuery({ queryKey: passwordSharesKey(workspaceId), queryFn: () => apiRequest<ShareDTO[]>(`/passwords/workspaces/${workspaceId}/shares`), enabled });
}

export async function passwordRecipientKeys(workspaceId: string, body: SharePasswordWorkspaceRequest): Promise<PasswordRecipientKeyDTO[]> {
  return apiRequest(`/passwords/workspaces/${workspaceId}/recipient-keys`, { method: "POST", body });
}

export function useSharePasswordWorkspace(workspaceId: string) {
  const client = useQueryClient();
  return useMutation<ShareDTO, Error, SharePasswordWorkspaceRequest>({ mutationFn: (body) => apiRequest(`/passwords/workspaces/${workspaceId}/shares`, { method: "POST", body }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordSharesKey(workspaceId) }) });
}

export function useRevokePasswordShare(workspaceId: string) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({ mutationFn: (shareId) => apiRequest(`/passwords/workspaces/${workspaceId}/shares/${shareId}`, { method: "DELETE" }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordSharesKey(workspaceId) }) });
}

export function usePasswordPublicLinks(workspaceId: string, entryId: string, enabled: boolean) {
  return useQuery({ queryKey: passwordPublicLinksKey(entryId), queryFn: () => apiRequest<PasswordPublicLinkDTO[]>(`/passwords/workspaces/${workspaceId}/entries/${entryId}/public-links`), enabled });
}

export function useCreatePasswordPublicLink(workspaceId: string, entryId: string) {
  const client = useQueryClient();
  return useMutation<CreatedPasswordPublicLinkDTO, Error, CreatePasswordPublicLinkRequest>({ mutationFn: (body) => apiRequest(`/passwords/workspaces/${workspaceId}/entries/${entryId}/public-links`, { method: "POST", body }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordPublicLinksKey(entryId) }) });
}

export function useRevokePasswordPublicLink(workspaceId: string, entryId: string) {
  const client = useQueryClient();
  return useMutation<void, Error, string>({ mutationFn: (linkId) => apiRequest(`/passwords/workspaces/${workspaceId}/entries/${entryId}/public-links/${linkId}`, { method: "DELETE" }), onSuccess: () => void client.invalidateQueries({ queryKey: passwordPublicLinksKey(entryId) }) });
}

export function readPublicPassword(token: string): Promise<PublicPasswordSecretDTO> {
  return apiRequest(`/passwords/public/${encodeURIComponent(token)}`, { tenant: null });
}
